import { Domain, DynamicPage } from '@superfunnel/types';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@superfunnel/ui-core/v2/Alert';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@superfunnel/ui-core/v2/Dialog';
import Input from '@superfunnel/ui-core/v2/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import { Tabs, TabsList, TabsTrigger } from '@superfunnel/ui-core/v2/Tabs';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { CheckIcon, CopyIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

interface Props {
  relevantDomains: QueryDocumentSnapshot<Domain>[] | undefined;
  primaryDomain: QueryDocumentSnapshot<Domain> | undefined;
  dynamicPage: DynamicPage | null | undefined;
  open: boolean;
  onClose: () => void;
}

export default function CopyDynamicPageLinkModal({
  relevantDomains,
  primaryDomain,
  dynamicPage,
  open,
  onClose,
}: Props) {
  const [selectedDomain, setSelectedDomain] =
    useState<QueryDocumentSnapshot<Domain>>();

  const [integration, setIntegration] = useState<
    'google-ads' | 'facebook-ads' | 'bing-ads'
  >('google-ads');

  const [copied, setCopied] = useState<{ link: boolean; final: boolean }>({
    link: false,
    final: false,
  });

  const link = useMemo(
    () =>
      `https://${(selectedDomain ?? primaryDomain)?.data().domain}/${
        dynamicPage?.path
      }`,
    [selectedDomain, primaryDomain, dynamicPage],
  );

  const finalUrl = useMemo(() => {
    switch (integration) {
      case 'google-ads':
        return `${link}?keyword={keyword}&gclid={gclid}&adid={creative}&adgroupid={adgroupid}&device={device}`;
      case 'facebook-ads':
        return `${link}?campaignid={{campaign.id}}&adgroupid={{adset.id}}&adid={{ad.id}}&placement={{placement}}`;
      case 'bing-ads':
        return `${link}?keyword={keyword}&msclkid={msclkid}&adid={AdId}&adgroupid={AdGroupId}&device={Device}&campaignid={CampaignId}`;
    }
  }, [integration, link]);

  const handleCopy = async (text: string, type: 'link' | 'final') => {
    await navigator.clipboard.writeText(text);
    setCopied({ ...copied, [type]: true });
    setTimeout(() => setCopied({ ...copied, [type]: false }), 2000);
  };

  const isLive = dynamicPage?.isLive;
  const hasChangesToPublish = dynamicPage?.hasChangesToPublish;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Copy Dynamic Page Link</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {!isLive && (
            <Alert variant="error">
              <AlertTitle>Page not published</AlertTitle>
              <AlertDescription>
                This page has never been published. The link will not work until
                you publish the page.
              </AlertDescription>
            </Alert>
          )}

          {isLive && hasChangesToPublish && (
            <Alert variant="warning">
              <AlertTitle>Unpublished changes</AlertTitle>
              <AlertDescription>
                You have unpublished changes. The live page may not reflect your
                latest edits.
              </AlertDescription>
            </Alert>
          )}

          {(relevantDomains?.length ?? 0) > 1 && (
            <div>
              <Select
                value={
                  selectedDomain?.id ||
                  relevantDomains?.find((rd) => rd.id === primaryDomain?.id)
                    ?.id ||
                  relevantDomains?.[0]?.id
                }
                onValueChange={(selected) => {
                  setSelectedDomain(
                    relevantDomains?.find((d) => d.id === selected),
                  );
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select domain" />
                </SelectTrigger>
                <SelectContent>
                  {relevantDomains?.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.data().domain}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Input
              title="Dynamic page link"
              value={link}
              readOnly
              endIcon={
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleCopy(link, 'link')}
                  className="h-6 w-6"
                >
                  {copied.link ? (
                    <CheckIcon className="h-4 w-4 text-green-600" />
                  ) : (
                    <CopyIcon className="h-4 w-4" />
                  )}
                </Button>
              }
            />
          </div>

          <div className="flex flex-col gap-3">
            <Tabs
              value={integration}
              onValueChange={(value) =>
                setIntegration(value as typeof integration)
              }
            >
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="google-ads">Google Ads</TabsTrigger>
                <TabsTrigger value="facebook-ads">Facebook Ads</TabsTrigger>
                <TabsTrigger value="bing-ads">Bing Ads</TabsTrigger>
              </TabsList>
            </Tabs>
            <Input
              title="Final URL with parameters"
              value={finalUrl}
              readOnly
              endIcon={
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleCopy(finalUrl, 'final')}
                  className="h-6 w-6"
                >
                  {copied.final ? (
                    <CheckIcon className="h-4 w-4 text-green-600" />
                  ) : (
                    <CopyIcon className="h-4 w-4" />
                  )}
                </Button>
              }
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
